// Game-specific progress tracking for Focus Catcher
// Stores scores, stars, combos, and reaction times in localStorage.
// Separate from the main progressTracking system (which handles streaks/badges).

const GAME_STORAGE_KEY = 'eyeTrainer_focusCatcher';

// ─── Achievement definitions ────────────────────────────────────────────────

const ACHIEVEMENTS = {
    focus_first:    { id: 'focus_first',    name: 'First Focus',     desc: 'Complete Level 1' },
    focus_5combo:   { id: 'focus_5combo',   name: 'Combo Starter',   desc: 'Reach a 5x combo' },
    focus_25combo:  { id: 'focus_25combo',  name: 'Combo Master',    desc: 'Reach a 25x combo' },
    focus_perfect:  { id: 'focus_perfect',  name: 'Perfectionist',   desc: '100% accuracy on any level' },
    focus_allstars: { id: 'focus_allstars', name: 'All Stars',       desc: '3-star all 15 levels' },
    focus_golden:   { id: 'focus_golden',   name: 'Gold Collector',  desc: 'Hit 10 golden targets' },
    focus_speed:    { id: 'focus_speed',    name: 'Lightning',       desc: 'Average reaction under 400ms' },
    focus_campaign: { id: 'focus_campaign', name: 'Champion',        desc: 'Complete all 15 levels' },
};

function getDefaultData() {
    return {
        levelStars: {},
        levelHighScores: {},
        endlessHighScore: 0,
        totalGamesPlayed: 0,
        bestCombo: 0,
        bestReactionTime: null,
        averageReactionTime: null,
        totalTargetsHit: 0,
        totalGoldenHits: 0,
        achievements: {},       // { achievementId: timestamp }
    };
}

function getGameData() {
    try {
        const stored = localStorage.getItem(GAME_STORAGE_KEY);
        if (stored) {
            return { ...getDefaultData(), ...JSON.parse(stored) };
        }
    } catch (e) {
        console.warn('Failed to load game data:', e);
    }
    return getDefaultData();
}

function saveGameData(data) {
    try {
        localStorage.setItem(GAME_STORAGE_KEY, JSON.stringify(data));
    } catch (e) {
        console.warn('Failed to save game data:', e);
    }
}

// Calculate star rating for a level result
// ★☆☆ = complete the level
// ★★☆ = score ≥ 70% of theoretical max
// ★★★ = score ≥ 90% of theoretical max AND accuracy ≥ 85%
function calculateStars(score, theoreticalMax, accuracy) {
    if (score >= theoreticalMax * 0.9 && accuracy >= 0.85) return 3;
    if (score >= theoreticalMax * 0.7) return 2;
    return 1;
}

// Estimate a theoretical max score for a level
// Assumes all targets hit with PERFECT speed bonus (3x) at max combo multiplier
function getTheoreticalMax(levelId) {
    // Rough estimate: ~60 targets per level at base 10 points
    // With average 2x speed bonus and 5x combo: 60 * 10 * 2 * 5 = 6000
    // Scale down for realistic achievability
    const baseTargets = 50 + levelId * 3;
    return baseTargets * 10 * 2.5 * 3; // approx
}

// Record completed game session
function recordGameSession(levelId, stats) {
    const data = getGameData();

    data.totalGamesPlayed += 1;
    data.totalTargetsHit += stats.targetsHit || 0;

    // Level-specific records
    if (levelId === 'endless') {
        // Endless mode — track high score
        if (stats.score > (data.endlessHighScore || 0)) {
            data.endlessHighScore = stats.score;
        }
    } else if (levelId === 'daily') {
        // Daily challenge — mark as completed
        data.lastDailyDate = new Date().toDateString();
    } else if (typeof levelId === 'number' && levelId > 0) {
        const theoreticalMax = getTheoreticalMax(levelId);
        const stars = calculateStars(stats.score, theoreticalMax, stats.accuracy);

        // Only save if better than previous
        if (!data.levelStars[levelId] || stars > data.levelStars[levelId]) {
            data.levelStars[levelId] = stars;
        }
        if (!data.levelHighScores[levelId] || stats.score > data.levelHighScores[levelId]) {
            data.levelHighScores[levelId] = stats.score;
        }
    }

    // Global records
    if (stats.bestCombo > data.bestCombo) {
        data.bestCombo = stats.bestCombo;
    }
    if (stats.avgReactionTime && (data.bestReactionTime === null || stats.avgReactionTime < data.bestReactionTime)) {
        data.bestReactionTime = Math.round(stats.avgReactionTime);
    }
    // Rolling average reaction time
    if (stats.avgReactionTime) {
        if (data.averageReactionTime === null) {
            data.averageReactionTime = Math.round(stats.avgReactionTime);
        } else {
            data.averageReactionTime = Math.round(data.averageReactionTime * 0.8 + stats.avgReactionTime * 0.2);
        }
    }

    // Track golden hits
    data.totalGoldenHits = (data.totalGoldenHits || 0) + (stats.goldenHits || 0);

    // Check achievements
    const newAchievements = checkAchievements(data, levelId, stats);

    saveGameData(data);
    return { data, newAchievements };
}

// Check and award achievements based on current state
function checkAchievements(data, levelId, stats) {
    if (!data.achievements) data.achievements = {};
    const newlyEarned = [];

    function award(id) {
        if (!data.achievements[id]) {
            data.achievements[id] = Date.now();
            newlyEarned.push(ACHIEVEMENTS[id]);
        }
    }

    // Complete Level 1
    if (data.levelStars[1] >= 1) award('focus_first');

    // Combo milestones
    if (data.bestCombo >= 5) award('focus_5combo');
    if (data.bestCombo >= 25) award('focus_25combo');

    // Perfect accuracy on just-played level
    if (stats && stats.accuracy >= 1.0 && stats.totalTargets >= 10) award('focus_perfect');

    // Golden target collector
    if (data.totalGoldenHits >= 10) award('focus_golden');

    // Speed demon
    if (stats && stats.avgReactionTime && stats.avgReactionTime < 400) award('focus_speed');

    // Complete all 15 levels (at least 1 star each)
    let allComplete = true;
    for (let i = 1; i <= 15; i++) {
        if (!data.levelStars[i] || data.levelStars[i] < 1) { allComplete = false; break; }
    }
    if (allComplete) award('focus_campaign');

    // 3-star all 15 levels
    let allThreeStars = true;
    for (let i = 1; i <= 15; i++) {
        if (!data.levelStars[i] || data.levelStars[i] < 3) { allThreeStars = false; break; }
    }
    if (allThreeStars) award('focus_allstars');

    return newlyEarned;
}

// Check if a level is unlocked
function isLevelUnlocked(levelId) {
    if (levelId <= 1) return true;
    const data = getGameData();
    return data.levelStars[levelId - 1] >= 1;
}

// Get the highest unlocked level
function getHighestUnlockedLevel() {
    const data = getGameData();
    let highest = 1;
    for (let i = 1; i <= 15; i++) {
        if (data.levelStars[i] >= 1) {
            highest = i + 1;
        } else {
            break;
        }
    }
    return highest;
}

export {
    GAME_STORAGE_KEY,
    ACHIEVEMENTS,
    getGameData,
    saveGameData,
    calculateStars,
    getTheoreticalMax,
    recordGameSession,
    checkAchievements,
    isLevelUnlocked,
    getHighestUnlockedLevel,
};
